import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { formatarEnderecoCompleto } from './cep';

// Dentro do APK (WebView Capacitor) nem o Web Share nem o doc.save() entregam o
// arquivo: o WebView nativo não tem gerenciador de download e não expõe
// navigator.share para arquivos. Gravamos o PDF via @capacitor/filesystem e
// abrimos o menu nativo via @capacitor/share (plugins presentes só no casco
// mobile/). Acessamos pelos globais que o Capacitor injeta — o frontend não
// importa os plugins, então o bundle web fica idêntico.
async function compartilharPDFNativo(doc, nomeArquivo, opcoesShare) {
  const { Filesystem, Share } = window.Capacitor?.Plugins || {};
  if (!Filesystem || !Share) {
    throw new Error('Plugins Capacitor Filesystem/Share indisponíveis no app');
  }
  // jsPDF → base64 puro (descarta o prefixo "data:application/pdf;...;base64,")
  const dataUri = doc.output('datauristring');
  const base64 = dataUri.slice(dataUri.indexOf(',') + 1);
  // Diretório de cache do app — não exige permissão de armazenamento
  const { uri } = await Filesystem.writeFile({ path: nomeArquivo, data: base64, directory: 'CACHE' });
  try {
    await Share.share({
      title: opcoesShare?.title || nomeArquivo,
      text: opcoesShare?.text,
      files: [uri],
      dialogTitle: opcoesShare?.dialogTitle || 'Compartilhar PDF',
    });
    return { ok: true, metodo: 'compartilhar-nativo' };
  } catch (err) {
    // Usuário fechou a folha de compartilhamento — não é falha real
    const msg = String(err?.message || err).toLowerCase();
    if (err?.name === 'AbortError' || msg.includes('cancel')) return { ok: true, metodo: 'cancelado' };
    throw err;
  }
}

// Tenta compartilhar via Web Share API (menu nativo do Android — WhatsApp, Drive,
// Salvar, etc.). Se não suportado, cai para o download tradicional (doc.save()).
// Não usamos window.open(blobUrl): em atalhos "adicionados à tela inicial" (que não
// são PWA de verdade), ele retorna sucesso mas a aba não aparece de verdade.
// Retorna { ok, metodo, erro } em vez de falhar silenciosamente.
async function compartilharOuAbrirPDF(doc, nomeArquivo, opcoesShare) {
  // APK (WebView Capacitor): entrega o arquivo pelos plugins nativos.
  if (window.Capacitor?.isNativePlatform?.()) {
    try {
      return await compartilharPDFNativo(doc, nomeArquivo, opcoesShare);
    } catch (err) {
      console.error('[PDF] Falha no compartilhamento nativo (Capacitor):', err);
      return { ok: false, erro: err };
    }
  }
  try {
    const blob = doc.output('blob');
    const file = new File([blob], nomeArquivo, { type: 'application/pdf' });
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], ...opcoesShare });
      return { ok: true, metodo: 'compartilhar' };
    }
  } catch (err) {
    if (err?.name === 'AbortError') return { ok: true, metodo: 'cancelado' }; // usuário fechou o menu de compartilhar
    console.error('[PDF] Falha ao compartilhar via Web Share API:', err);
  }
  try {
    doc.save(nomeArquivo);
    return { ok: true, metodo: 'download' };
  } catch (err) {
    console.error('[PDF] Falha ao baixar:', err);
    return { ok: false, erro: err };
  }
}

const STATUS_LABEL = {
  orcamento: 'AG. AVALIAÇÃO DO MECÂNICO',
  aprovado: 'APROVADO',
  em_andamento: 'EM ANDAMENTO',
  aguardando_orcamento: 'ENVIAR ORÇAMENTO PARA APROVAÇÃO',
  aguardando_cliente:   'AGUARDANDO APROVAÇÃO DO CLIENTE',
  concluido: 'CONCLUÍDO',
  cancelado: 'ORÇAMENTO NÃO APROVADO',
};

export function gerarPDFServico(servico, config, opcoesShare) {
  const doc = new jsPDF();
  const larPagina = doc.internal.pageSize.getWidth();
  const altPagina = doc.internal.pageSize.getHeight();
  let y = 15;

  // Cria uma nova página quando o conteúdo restante não cabe mais na atual —
  // evita que blocos (totais, observações, assinatura) sejam cortados no rodapé.
  const quebrarSeNecessario = (alturaNecessaria = 15) => {
    if (y + alturaNecessaria > altPagina - 20) {
      doc.addPage();
      y = 20;
    }
  };

  // Logo
  if (config?.logoBase64) {
    try {
      doc.addImage(config.logoBase64, 'PNG', 14, y, 30, 20);
    } catch {}
    y = 40;
  }

  // Cabeçalho oficina
  doc.setFontSize(16).setFont(undefined, 'bold');
  doc.text(config?.nomeOficina || 'Oficina Mecânica', config?.logoBase64 ? 50 : 14, config?.logoBase64 ? 22 : y);
  doc.setFontSize(9).setFont(undefined, 'normal');
  if (config?.cnpj) doc.text(`CNPJ: ${config.cnpj}`, config?.logoBase64 ? 50 : 14, config?.logoBase64 ? 28 : y + 6);
  if (config?.endereco) doc.text(config.endereco, config?.logoBase64 ? 50 : 14, config?.logoBase64 ? 33 : y + 12);
  if (config?.telefone) doc.text(`Tel: ${config.telefone}`, config?.logoBase64 ? 50 : 14, config?.logoBase64 ? 38 : y + 18);

  // Título
  y = 50;
  doc.setFontSize(14).setFont(undefined, 'bold');
  doc.text(`${STATUS_LABEL[servico.status] || 'SERVIÇO'} Nº ${servico.numero}`, 14, y);
  doc.setFontSize(9).setFont(undefined, 'normal');
  doc.text(`Data: ${format(new Date(servico.dataEntrada), 'dd/MM/yyyy', { locale: ptBR })}`, larPagina - 14, y, { align: 'right' });

  // Linha separadora
  y += 5;
  doc.setDrawColor(30, 64, 175).setLineWidth(0.5).line(14, y, larPagina - 14, y);
  y += 8;

  // Dados cliente/veículo — colunas independentes (alturas variam conforme os dados disponíveis)
  const COMBUSTIVEL_LABEL = { flex: 'Flex', gasolina: 'Gasolina', etanol: 'Etanol', diesel: 'Diesel', eletrico: 'Elétrico', hibrido: 'Híbrido' };
  const colDir = larPagina / 2;
  let yEsq = y, yDir = y;

  // Larguras máximas de cada coluna — texto mais longo que isso quebra em nova
  // linha em vez de invadir a coluna vizinha (ex: nome/telefone longos por cima
  // dos dados do veículo).
  const colEsqLargura = colDir - 14 - 6;
  const colDirLargura = larPagina - colDir - 14;

  doc.setFontSize(10).setFont(undefined, 'bold').text('CLIENTE', 14, yEsq); yEsq += 5;
  doc.setFont(undefined, 'normal').setFontSize(9);
  const linhasNomeTel = doc.splitTextToSize(`Nome: ${servico.cliente?.nome || ''}   Tel: ${servico.cliente?.telefone || ''}`, colEsqLargura);
  doc.text(linhasNomeTel, 14, yEsq); yEsq += linhasNomeTel.length * 5;
  if (servico.cliente?.email) {
    const linhasEmail = doc.splitTextToSize(`Email: ${servico.cliente.email}`, colEsqLargura);
    doc.text(linhasEmail, 14, yEsq); yEsq += linhasEmail.length * 5;
  }
  const enderecoCliente = formatarEnderecoCompleto(servico.cliente || {});
  if (enderecoCliente) {
    const linhasEndereco = doc.splitTextToSize(`Endereço: ${enderecoCliente}`, colEsqLargura);
    doc.text(linhasEndereco, 14, yEsq); yEsq += linhasEndereco.length * 5;
  }

  doc.setFontSize(10).setFont(undefined, 'bold').text('VEÍCULO', colDir, y); yDir += 5;
  doc.setFont(undefined, 'normal').setFontSize(9);
  const linhasVeiculo = doc.splitTextToSize(`${servico.veiculo?.marca || ''} ${servico.veiculo?.modelo || ''} ${servico.veiculo?.ano || ''}`, colDirLargura);
  doc.text(linhasVeiculo, colDir, yDir); yDir += linhasVeiculo.length * 5;
  const linhasPlacaKm = doc.splitTextToSize(`Placa: ${servico.veiculo?.placa || ''}   KM: ${servico.kmEntrada || ''}`, colDirLargura);
  doc.text(linhasPlacaKm, colDir, yDir); yDir += linhasPlacaKm.length * 5;
  const linhasCor = doc.splitTextToSize(`Cor: ${servico.veiculo?.cor || ''}   Combustível: ${COMBUSTIVEL_LABEL[servico.veiculo?.combustivel] || servico.veiculo?.combustivel || ''}`, colDirLargura);
  doc.text(linhasCor, colDir, yDir); yDir += linhasCor.length * 5;
  if (servico.responsavel) {
    doc.setFont(undefined, 'bold').text('Mecânico:', colDir, yDir);
    doc.setFont(undefined, 'normal').text(` ${servico.responsavel}`, colDir + 20, yDir); yDir += 5;
  }

  y = Math.max(yEsq, yDir) + 5;

  // Diagnóstico cliente
  if (servico.diagnostico) {
    quebrarSeNecessario(20);
    doc.setFont(undefined, 'bold').setFontSize(10).text('DIAGNÓSTICO / PROBLEMA RELATADO — CLIENTE:', 14, y); y += 5;
    doc.setFont(undefined, 'normal').setFontSize(9);
    const linhasDiag = doc.splitTextToSize(servico.diagnostico, larPagina - 28);
    doc.text(linhasDiag, 14, y); y += linhasDiag.length * 5 + 4;
  }

  // Diagnóstico mecânico
  if (servico.diagnosticoMecanico) {
    quebrarSeNecessario(20);
    doc.setFont(undefined, 'bold').setFontSize(10).text('DIAGNÓSTICO — MECÂNICO:', 14, y); y += 5;
    doc.setFont(undefined, 'normal').setFontSize(9);
    const linhasMec = doc.splitTextToSize(servico.diagnosticoMecanico, larPagina - 28);
    doc.text(linhasMec, 14, y); y += linhasMec.length * 5 + 5;
  }

  // Peças
  if (servico.pecas?.length > 0) {
    quebrarSeNecessario(20);
    doc.setFont(undefined, 'bold').setFontSize(10).text('PEÇAS E MATERIAIS', 14, y); y += 3;
    autoTable(doc, {
      startY: y,
      head: [[
        'Descrição',
        { content: 'Qtd', styles: { halign: 'center' } },
        { content: 'Valor Unit.', styles: { halign: 'right' } },
        { content: 'Total', styles: { halign: 'right' } },
      ]],
      body: servico.pecas.map(p => [
        p.descricao,
        p.quantidade,
        `R$ ${p.valorUnitario.toFixed(2)}`,
        `R$ ${p.valorTotal.toFixed(2)}`,
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [30, 64, 175] },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // Mão de obra
  if (servico.maosDeObra?.length > 0) {
    quebrarSeNecessario(20);
    doc.setFont(undefined, 'bold').setFontSize(10).text('MÃO DE OBRA', 14, y); y += 3;
    autoTable(doc, {
      startY: y,
      head: [['Descrição', { content: 'Valor', styles: { halign: 'right' } }]],
      body: servico.maosDeObra.map(m => [m.descricao, `R$ ${m.valor.toFixed(2)}`]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [30, 64, 175] },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // Totais
  quebrarSeNecessario(30);
  doc.setDrawColor(200).setLineWidth(0.3).line(14, y, larPagina - 14, y); y += 5;
  doc.setFontSize(10).setFont(undefined, 'normal');
  doc.text(`Total Peças: R$ ${servico.totalPecas?.toFixed(2)}`, larPagina - 14, y, { align: 'right' }); y += 5;
  doc.text(`Total Mão de Obra: R$ ${servico.totalMaoDeObra?.toFixed(2)}`, larPagina - 14, y, { align: 'right' }); y += 5;
  if (servico.desconto > 0) {
    doc.text(`Desconto: R$ ${servico.desconto.toFixed(2)}`, larPagina - 14, y, { align: 'right' }); y += 5;
  }
  doc.setFont(undefined, 'bold').setFontSize(12);
  doc.text(`TOTAL: R$ ${servico.totalGeral?.toFixed(2)}`, larPagina - 14, y, { align: 'right' });

  // Termo de autorização
  y += 10;
  const larTexto = larPagina - 28;
  const clausulas = [
    'Autorizo a realização dos serviços especificados acima, assumindo os custos decorrentes tanto da troca de peças quanto da mão de obra empregada.',
    'Se necessário esta autorizada a realização de Teste de Rodagem externo com veículo.',
    'Podem ocorrer adição de peças e complementos no orçamento após a montagem do veículo.',
    'Prazo de Garantia de 90 dias. Conforme Código de Defesa do Consumidor, Seção IV, Art. 26, II — noventa dias, tratando-se de fornecimento de serviços e de produtos duráveis (peças inclusas).',
  ];
  doc.setFont(undefined, 'normal').setFontSize(8);
  clausulas.forEach((clausula, i) => {
    const linhas = doc.splitTextToSize(`${i + 1}. ${clausula}`, larTexto);
    quebrarSeNecessario(linhas.length * 4 + 2);
    doc.text(linhas, 14, y); y += linhas.length * 4 + 2;
  });

  // Observações
  if (servico.observacoes) {
    y += 10;
    const obs = doc.splitTextToSize(servico.observacoes, larPagina - 28);
    quebrarSeNecessario(10 + obs.length * 5);
    doc.setFont(undefined, 'bold').setFontSize(9).text('Observações:', 14, y); y += 5;
    doc.setFont(undefined, 'normal');
    doc.text(obs, 14, y); y += obs.length * 5;
  }

  // Assinatura — fica perto do rodapé da página atual, mas se o conteúdo já
  // chegou perto do fim, abre uma nova página em vez de sobrepor o texto.
  const yRodape = altPagina - 25;
  if (y + 15 > yRodape) doc.addPage();
  y = yRodape;
  doc.setDrawColor(100).setLineWidth(0.3).line(14, y, 90, y);
  doc.setFontSize(8).setFont(undefined, 'normal').text('Nome legível / Assinatura do cliente', 14, y + 5);

  return compartilharOuAbrirPDF(doc, `orcamento-${servico.numero}.pdf`, opcoesShare);
}

export function gerarPDFHistorico(veiculo, servicos, config, isAdmin) {
  const doc = new jsPDF({ orientation: 'landscape' });
  const larPagina = doc.internal.pageSize.getWidth();
  let y = 15;

  if (config?.logoBase64) {
    try { doc.addImage(config.logoBase64, 'PNG', 14, y, 28, 18); } catch {}
  }
  const tituloX = config?.logoBase64 ? 48 : 14;
  doc.setFontSize(14).setFont(undefined, 'bold');
  doc.text(config?.nomeOficina || 'Oficina Mecânica', tituloX, y + 5);
  doc.setFontSize(10).setFont(undefined, 'normal');
  doc.text('Histórico do Veículo — Peças, Garantias e Orçamentos Anteriores', tituloX, y + 12);
  doc.setFontSize(8).setTextColor(150);
  doc.text(`Gerado em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`, larPagina - 14, y + 5, { align: 'right' });
  doc.setTextColor(0);

  y = 38;
  doc.setDrawColor(30, 64, 175).setLineWidth(0.5).line(14, y, larPagina - 14, y);
  y += 8;

  // Dados do veículo
  doc.setFontSize(11).setFont(undefined, 'bold');
  doc.text(`${veiculo.placa || '(sem placa)'} — ${veiculo.marca || ''} ${veiculo.modelo || ''} ${veiculo.ano || ''}`, 14, y);
  y += 6;
  doc.setFontSize(9).setFont(undefined, 'normal');
  if (veiculo.cliente?.nome) { doc.text(`Proprietário: ${veiculo.cliente.nome}${veiculo.cliente.telefone ? ' — ' + veiculo.cliente.telefone : ''}`, 14, y); y += 5; }
  const concluidas = servicos.filter(s => s.status === 'concluido');
  const totalInvestido = concluidas.reduce((a, s) => a + (s.totalGeral || 0), 0);
  doc.text(`Total de OS: ${servicos.length}  •  Concluídas: ${concluidas.length}` + (isAdmin ? `  •  Total investido: R$ ${totalInvestido.toFixed(2)}` : ''), 14, y);
  y += 8;

  // Peças trocadas e garantias
  const todasPecas = servicos
    .flatMap(s => (s.pecas || []).map(p => ({ ...p, servicoNumero: s.numero, servicoData: s.dataEntrada })))
    .sort((a, b) => new Date(b.servicoData) - new Date(a.servicoData));

  doc.setFontSize(11).setFont(undefined, 'bold');
  doc.text('PEÇAS TROCADAS E GARANTIAS', 14, y);
  y += 3;

  const garantiaTexto = g => {
    if (!g?.fornecedor) return '—';
    if (!g.dataCompra || !g.diasGarantia) return g.fornecedor;
    const venc = new Date(g.dataCompra + 'T00:00:00');
    venc.setDate(venc.getDate() + Number(g.diasGarantia));
    const diff = Math.ceil((venc - new Date()) / 86400000);
    return diff < 0 ? `Vencida (${venc.toLocaleDateString('pt-BR')})` : `Válida até ${venc.toLocaleDateString('pt-BR')}`;
  };

  const headPecas = isAdmin
    ? ['Peça', 'Qtd', 'OS', 'Data', 'Valor', 'Fornecedor', 'NF', 'Garantia']
    : ['Peça', 'Qtd', 'OS', 'Data', 'Fornecedor', 'NF', 'Garantia'];

  const bodyPecas = todasPecas.length > 0 ? todasPecas.map(p => {
    const base = [
      p.descricao || '',
      String(p.quantidade || 1),
      `#${p.servicoNumero}`,
      p.servicoData ? format(new Date(p.servicoData), 'dd/MM/yyyy', { locale: ptBR }) : '—',
    ];
    if (isAdmin) base.push(`R$ ${(p.valorTotal || 0).toFixed(2)}`);
    base.push(p.garantia?.fornecedor || '—', p.garantia?.notaFiscal || '—', garantiaTexto(p.garantia));
    return base;
  }) : [[{ content: 'Nenhuma peça registrada para este veículo', colSpan: headPecas.length, styles: { halign: 'center', textColor: 150 } }]];

  autoTable(doc, {
    startY: y,
    head: [headPecas],
    body: bodyPecas,
    styles: { fontSize: 8, cellPadding: 2.5 },
    headStyles: { fillColor: [30, 64, 175] },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Ordens de serviço
  if (y > doc.internal.pageSize.getHeight() - 40) { doc.addPage(); y = 15; }
  doc.setFontSize(11).setFont(undefined, 'bold');
  doc.text('ORDENS DE SERVIÇO', 14, y);
  y += 3;

  const headOS = isAdmin
    ? ['OS', 'Data', 'KM', 'Mecânico', 'Status', 'Prob. Relatado (Cliente)', 'Diag. Mecânico', 'Total']
    : ['OS', 'Data', 'KM', 'Mecânico', 'Status', 'Prob. Relatado (Cliente)', 'Diag. Mecânico'];
  const bodyOS = servicos.map(s => {
    const base = [
      `#${s.numero}`,
      s.dataEntrada ? format(new Date(s.dataEntrada), 'dd/MM/yyyy', { locale: ptBR }) : '—',
      s.kmEntrada ? Number(s.kmEntrada).toLocaleString('pt-BR') : '—',
      s.responsavel || '—',
      STATUS_LABEL[s.status] || s.status,
      s.diagnostico || '—',
      s.diagnosticoMecanico || '—',
    ];
    if (isAdmin) base.push(`R$ ${(s.totalGeral || 0).toFixed(2)}`);
    return base;
  });

  autoTable(doc, {
    startY: y,
    head: [headOS],
    body: bodyOS,
    styles: { fontSize: 7.5, cellPadding: 2.5 },
    headStyles: { fillColor: [30, 64, 175], fontSize: 7.5 },
    columnStyles: {
      0: { cellWidth: 12 },
      1: { cellWidth: 18 },
      2: { cellWidth: 14 },
      3: { cellWidth: 28 },
      4: { cellWidth: 22 },
      5: { cellWidth: 40 },
      6: { cellWidth: 40 },
    },
    margin: { left: 14, right: 14 },
  });

  return compartilharOuAbrirPDF(doc, `historico-${(veiculo.placa || 'veiculo').replace(/\s+/g, '')}.pdf`);
}

export function gerarPDFPecas(servicos, mes, ano, config) {
  const doc = new jsPDF({ orientation: 'landscape' });
  const larPagina = doc.internal.pageSize.getWidth();
  let y = 15;

  // Logo
  if (config?.logoBase64) {
    try { doc.addImage(config.logoBase64, 'PNG', 14, y, 25, 16); } catch {}
  }

  // Cabeçalho
  const nomeMes = mes === -1 ? 'Todos os Meses' : new Date(ano, mes, 1).toLocaleString('pt-BR', { month: 'long' });
  const periodoLabel = mes === -1 ? `Todos os Meses — ${ano}` : `${nomeMes.charAt(0).toUpperCase() + nomeMes.slice(1)} / ${ano}`;
  const tituloX = config?.logoBase64 ? 44 : 14;
  doc.setFontSize(14).setFont(undefined, 'bold');
  doc.text(config?.nomeOficina || 'Oficina Mecânica', tituloX, y + 5);
  doc.setFontSize(10).setFont(undefined, 'normal');
  doc.text(`Relatório de Peças Trocadas — ${periodoLabel}`, tituloX, y + 12);
  doc.text(`Gerado em: ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`, larPagina - 14, y + 5, { align: 'right' });

  y = 40;
  doc.setDrawColor(30, 64, 175).setLineWidth(0.5).line(14, y, larPagina - 14, y);
  y += 8;

  // Monta linhas agrupadas por OS
  const linhas = [];
  let totalQtd = 0;
  let totalCusto = 0;
  let totalVenda = 0;
  const todosOsMeses = mes === -1;

  servicos.forEach(s => {
    if (!(s.pecas || []).length) return;

    const veiculo = [s.veiculo?.marca, s.veiculo?.modelo, s.veiculo?.ano].filter(Boolean).join(' ');
    const mesDaOS = new Date(s.updatedAt || s.createdAt).toLocaleString('pt-BR', { month: 'long' });
    const dataOS  = new Date(s.updatedAt || s.createdAt).toLocaleDateString('pt-BR');

    // Cabeçalho da OS inserido como prefixo na coluna descrição da 1ª peça
    const kmTxt  = s.kmEntrada ? `KM: ${Number(s.kmEntrada).toLocaleString('pt-BR')}` : '';
    const mecTxt = s.responsavel ? `Mecânico: ${s.responsavel}` : '';
    const osHeader = [`OS #${s.numero}`, dataOS, veiculo, kmTxt, mecTxt].filter(Boolean).join('  |  ');
    const diagCliente = s.diagnostico?.trim() ? `Problema relatado: ${s.diagnostico.trim()}` : '';
    const diagMec     = s.diagnosticoMecanico?.trim() ? `Diag. mecânico: ${s.diagnosticoMecanico.trim()}` : '';

    (s.pecas || []).forEach((p, idx) => {
      const qtd = p.quantidade || 1;
      const venda = p.valorUnitario || 0;
      const custo = p.custoUnitario || 0;
      const totalLinha = p.valorTotal || venda * qtd;
      const totalCustoLinha = custo * qtd;
      const margem = custo > 0 && venda > 0
        ? (((venda - custo) / venda) * 100).toFixed(1) + '%'
        : '—';

      // Na primeira peça de cada OS, inclui info da OS acima da descrição
      const extras = idx === 0 ? [osHeader, diagCliente, diagMec].filter(Boolean) : [];
      const descCell = extras.length
        ? {
            content: [...extras, p.descricao].join('\n'),
            styles: { fontStyle: 'normal', fontSize: 7.5 },
          }
        : p.descricao;

      const linha = todosOsMeses
        ? [mesDaOS, descCell, veiculo, qtd,
            custo > 0 ? `R$ ${custo.toFixed(2)}` : '—',
            custo > 0 ? `R$ ${totalCustoLinha.toFixed(2)}` : '—',
            `R$ ${venda.toFixed(2)}`, margem, `R$ ${totalLinha.toFixed(2)}`]
        : [descCell, veiculo, qtd,
            custo > 0 ? `R$ ${custo.toFixed(2)}` : '—',
            custo > 0 ? `R$ ${totalCustoLinha.toFixed(2)}` : '—',
            `R$ ${venda.toFixed(2)}`, margem, `R$ ${totalLinha.toFixed(2)}`];

      linhas.push(linha);
      totalQtd += qtd;
      totalCusto += totalCustoLinha;
      totalVenda += totalLinha;
    });
  });

  // Linha subtotal
  const subtotalColSpan = todosOsMeses ? 3 : 2;
  linhas.push([
    { content: 'TOTAL PEÇAS', colSpan: subtotalColSpan, styles: { fontStyle: 'bold', textColor: [30, 41, 59], fillColor: [226, 232, 240] } },
    { content: totalQtd, styles: { fontStyle: 'bold', fillColor: [226, 232, 240], halign: 'center' } },
    { content: '—', styles: { fillColor: [226, 232, 240], halign: 'right' } },
    { content: totalCusto > 0 ? `R$ ${totalCusto.toFixed(2)}` : '—', styles: { fontStyle: 'bold', fillColor: [226, 232, 240], halign: 'right' } },
    { content: '—', styles: { fillColor: [226, 232, 240], halign: 'right' } },
    { content: totalCusto > 0 ? (((totalVenda - totalCusto) / totalVenda) * 100).toFixed(1) + '%' : '—', styles: { fontStyle: 'bold', fillColor: [226, 232, 240], halign: 'center' } },
    { content: `R$ ${totalVenda.toFixed(2)}`, styles: { fontStyle: 'bold', fillColor: [226, 232, 240], halign: 'right' } },
  ]);

  const lucroTotal = totalVenda - totalCusto;

  const headPDF = todosOsMeses
    ? ['Mês', 'Peça', 'Veículo', 'Qtd', 'Custo Unit.*', 'Custo Total', 'Venda Unit.', 'Margem', 'Total (R$)']
    : ['Peça', 'Veículo (Marca / Modelo / Ano)', 'Qtd', 'Custo Unit.*', 'Custo Total', 'Venda Unit.', 'Margem', 'Total (R$)'];

  const colStylesPDF = todosOsMeses ? {
    0: { cellWidth: 18 },
    1: { cellWidth: 52 },
    2: { cellWidth: 46 },
    3: { cellWidth: 12, halign: 'center' },
    4: { cellWidth: 22, halign: 'right' },
    5: { cellWidth: 22, halign: 'right' },
    6: { cellWidth: 22, halign: 'right' },
    7: { cellWidth: 18, halign: 'center' },
    8: { cellWidth: 24, halign: 'right' },
  } : {
    0: { cellWidth: 62 },
    1: { cellWidth: 60 },
    2: { cellWidth: 14, halign: 'center' },
    3: { cellWidth: 26, halign: 'right' },
    4: { cellWidth: 26, halign: 'right' },
    5: { cellWidth: 26, halign: 'right' },
    6: { cellWidth: 22, halign: 'center' },
    7: { cellWidth: 28, halign: 'right' },
  };

  autoTable(doc, {
    startY: y,
    head: [headPDF],
    body: linhas,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [30, 64, 175], fontSize: 8, fontStyle: 'bold' },
    columnStyles: colStylesPDF,
    margin: { left: 14, right: 14 },
  });

  // Nota de rodapé
  const finalY = doc.lastAutoTable.finalY + 6;
  doc.setFontSize(7).setFont(undefined, 'italic').setTextColor(150);
  doc.text('* Custo interno — documento confidencial, não compartilhar com clientes.', 14, finalY);
  if (lucroTotal > 0) {
    doc.text(`Lucro estimado sobre peças: R$ ${lucroTotal.toFixed(2)}`, larPagina - 14, finalY, { align: 'right' });
  }

  const nomeArquivo = mes === -1 ? `pecas-trocadas-${ano}` : `pecas-trocadas-${nomeMes}-${ano}`;
  return compartilharOuAbrirPDF(doc, `${nomeArquivo}.pdf`);
}

export function gerarExcelPecas(servicos, mes, ano, config) {
  const nomeMes = mes === -1 ? 'Todos os Meses' : new Date(ano, mes, 1).toLocaleString('pt-BR', { month: 'long' });
  const periodoLabel = mes === -1 ? `Todos os Meses - ${ano}` : `${nomeMes} - ${ano}`;

  const linhas = [];
  let totalQtd = 0, totalCusto = 0, totalVenda = 0;

  const todosOsMesesXls = mes === -1;

  servicos.forEach(s => {
    const veiculo = [s.veiculo?.marca, s.veiculo?.modelo, s.veiculo?.ano].filter(Boolean).join(' ');
    const mesDaOS = new Date(s.updatedAt).toLocaleString('pt-BR', { month: 'long' });
    (s.pecas || []).forEach(p => {
      const qtd = p.quantidade || 1;
      const venda = p.valorUnitario || 0;
      const custo = p.custoUnitario || 0;
      const totalLinha = p.valorTotal || venda * qtd;
      const totalCustoLinha = custo * qtd;
      const margem = custo > 0 && venda > 0 ? ((venda - custo) / venda) * 100 : null;

      const obj = {};
      if (todosOsMesesXls) obj['Mês'] = mesDaOS;
      obj['Peça'] = p.descricao;
      obj['Veículo'] = veiculo;
      obj['OS'] = `OS #${s.numero}`;
      obj['Qtd'] = qtd;
      obj['Custo Unit. (R$)'] = custo > 0 ? custo : '';
      obj['Custo Total (R$)'] = custo > 0 ? totalCustoLinha : '';
      obj['Venda Unit. (R$)'] = venda;
      linhas.push({
        ...obj,
        'Total Venda (R$)': totalLinha,
        'Margem (%)': margem !== null ? parseFloat(margem.toFixed(1)) : '',
      });

      totalQtd += qtd;
      totalCusto += totalCustoLinha;
      totalVenda += totalLinha;
    });
  });

  // Linha de totais
  linhas.push({});
  const totalRow = {};
  if (todosOsMesesXls) totalRow['Mês'] = '';
  totalRow['Peça'] = 'TOTAL';
  totalRow['Veículo'] = '';
  totalRow['OS'] = '';
  totalRow['Qtd'] = totalQtd;
  totalRow['Custo Unit. (R$)'] = '';
  totalRow['Custo Total (R$)'] = totalCusto > 0 ? totalCusto : '';
  totalRow['Venda Unit. (R$)'] = '';
  totalRow['Total Venda (R$)'] = totalVenda;
  totalRow['Margem (%)'] = totalCusto > 0 && totalVenda > 0 ? parseFloat((((totalVenda - totalCusto) / totalVenda) * 100).toFixed(1)) : '';
  linhas.push(totalRow);

  const ws = XLSX.utils.json_to_sheet(linhas);

  // Larguras das colunas
  ws['!cols'] = todosOsMesesXls
    ? [{ wch: 14 }, { wch: 35 }, { wch: 30 }, { wch: 12 }, { wch: 6 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 12 }]
    : [{ wch: 35 }, { wch: 30 }, { wch: 12 }, { wch: 6 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 12 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, periodoLabel.slice(0, 31));

  // Aba de resumo por peça + veículo (+ mês quando todos os meses)
  const mapaResumo = {};
  servicos.forEach(s => {
    const veiculo = [s.veiculo?.marca, s.veiculo?.modelo, s.veiculo?.ano].filter(Boolean).join(' ');
    const mesDaOS = new Date(s.updatedAt).toLocaleString('pt-BR', { month: 'long' });
    (s.pecas || []).forEach(p => {
      const key = todosOsMesesXls
        ? `${mesDaOS}|${p.descricao.trim().toLowerCase()}|${veiculo.toLowerCase()}`
        : `${p.descricao.trim().toLowerCase()}|${veiculo.toLowerCase()}`;
      if (!mapaResumo[key]) {
        const base = {};
        if (todosOsMesesXls) base['Mês'] = mesDaOS;
        base['Peça'] = p.descricao;
        base['Veículo'] = veiculo;
        base['Qtd Total'] = 0;
        base['Custo Unit. (R$)'] = p.custoUnitario || 0;
        base['Custo Total (R$)'] = 0;
        base['Nº OS'] = 0;
        mapaResumo[key] = base;
      }
      mapaResumo[key]['Qtd Total'] += p.quantidade || 1;
      mapaResumo[key]['Custo Total (R$)'] += (p.custoUnitario || 0) * (p.quantidade || 1);
      mapaResumo[key]['Nº OS'] += 1;
    });
  });
  const resumo = Object.values(mapaResumo).sort((a, b) => b['Qtd Total'] - a['Qtd Total']);
  const wsResumo = XLSX.utils.json_to_sheet(resumo);
  wsResumo['!cols'] = todosOsMesesXls
    ? [{ wch: 14 }, { wch: 35 }, { wch: 32 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 8 }]
    : [{ wch: 35 }, { wch: 32 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 8 }];
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo por Peça');

  const nomeArquivo = mes === -1 ? `pecas-trocadas-${ano}` : `pecas-trocadas-${nomeMes}-${ano}`;
  XLSX.writeFile(wb, `${nomeArquivo}.xlsx`);
}

export function gerarPDFDetalhamento(servicos, label, config) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const nomeOficina = config?.nomeOficina || 'Oficina Mecânica';
  const PW = 277; // largura útil landscape A4 (297 - 2*10)

  // Cabeçalho
  doc.setFontSize(14); doc.setFont(undefined, 'bold');
  doc.text(nomeOficina, 14, 16);
  doc.setFontSize(10); doc.setFont(undefined, 'normal');
  doc.text(`Detalhamento Financeiro — ${label}`, 14, 23);
  doc.setFontSize(8); doc.setTextColor(150);
  doc.text(`Emitido em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`, 14, 29);
  doc.setTextColor(0);

  // Totais resumidos
  const totalGeral = servicos.reduce((a, s) => a + (s.totalGeral || 0), 0);
  const totalPecas = servicos.reduce((a, s) => a + (s.totalPecas || 0), 0);
  const totalMao   = servicos.reduce((a, s) => a + (s.totalMaoDeObra || 0), 0);
  const totalDesc  = servicos.reduce((a, s) => a + (s.desconto || 0), 0);
  // Custo total = custo de peças (custoUnitario × quantidade) + custo de mão de obra
  // (custoUnitario por serviço, ex.: repasse ao mecânico) — margem sobre o faturamento total da OS.
  const calcCusto  = s => (
    (s.pecas || []).reduce((a, p) => a + (p.custoUnitario || 0) * (p.quantidade || 1), 0) +
    (s.maosDeObra || []).reduce((a, m) => a + (m.custoUnitario || 0), 0)
  );
  const totalCusto = servicos.reduce((a, s) => a + calcCusto(s), 0);
  const lucro      = totalGeral - totalCusto;
  const margem     = totalGeral > 0 && totalCusto > 0 ? (lucro / totalGeral) * 100 : null;

  const resumo = [
    ['Faturamento Total', `R$ ${totalGeral.toFixed(2)}`],
    ['Peças (venda)', `R$ ${totalPecas.toFixed(2)}`],
    ['Mão de Obra', `R$ ${totalMao.toFixed(2)}`],
    ['Descontos', `- R$ ${totalDesc.toFixed(2)}`],
    ['Custo Total (Peças + M. Obra)', `R$ ${totalCusto.toFixed(2)}`],
    ['Lucro Total', `R$ ${lucro.toFixed(2)}`],
    ['Margem Total', margem !== null ? `${margem.toFixed(1)}%` : '—'],
    ['Qtd OS Concluídas', String(servicos.length)],
    ['Ticket Médio', servicos.length > 0 ? `R$ ${(totalGeral / servicos.length).toFixed(2)}` : '—'],
  ];

  // Resumo em duas colunas lado a lado
  const col1 = resumo.slice(0, Math.ceil(resumo.length / 2));
  const col2 = resumo.slice(Math.ceil(resumo.length / 2));
  autoTable(doc, {
    startY: 34,
    head: [['Indicador', 'Valor', 'Indicador', 'Valor']],
    body: col1.map((r, i) => [...r, ...(col2[i] || ['', ''])]),
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [30, 64, 175], fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 55 }, 1: { cellWidth: 40, halign: 'right' },
      2: { cellWidth: 55 }, 3: { cellWidth: 40, halign: 'right' },
    },
    margin: { left: 10, right: 10 },
  });

  // Lista de OS
  const fmt2 = v => v.toFixed(2).replace('.', ',');
  const linhasOS = [...servicos]
    .sort((a, b) => b.totalGeral - a.totalGeral)
    .map(s => {
      const custo = calcCusto(s);
      const margemOS = custo > 0 && s.totalGeral > 0 ? ((s.totalGeral - custo) / s.totalGeral * 100).toFixed(1) + '%' : '—';
      const veiculo = s.veiculo ? `${s.veiculo.placa} • ${s.veiculo.marca} ${s.veiculo.modelo}`.slice(0, 42) : '—';
      return [
        `OS #${s.numero}`,
        s.cliente?.nome || '—',
        veiculo,
        `R$ ${fmt2(s.totalPecas || 0)}`,
        `R$ ${fmt2(s.totalMaoDeObra || 0)}`,
        s.desconto ? `- R$ ${fmt2(s.desconto)}` : '—',
        `R$ ${fmt2(s.totalGeral || 0)}`,
        margemOS,
      ];
    });

  // Linha de totais
  const totalGeral2 = servicos.reduce((a, s) => a + (s.totalGeral || 0), 0);
  const totalPecas2 = servicos.reduce((a, s) => a + (s.totalPecas || 0), 0);
  const totalMao2   = servicos.reduce((a, s) => a + (s.totalMaoDeObra || 0), 0);
  const totalDesc2  = servicos.reduce((a, s) => a + (s.desconto || 0), 0);
  const totalCusto2 = servicos.reduce((a, s) => a + calcCusto(s), 0);
  const lucro2  = totalGeral2 - totalCusto2;
  const margem2 = totalGeral2 > 0 && totalCusto2 > 0 ? (lucro2 / totalGeral2 * 100).toFixed(1) + '%' : '—';
  linhasOS.push([
    { content: 'TOTAL', styles: { fontStyle: 'bold', fillColor: [226, 232, 240] } },
    { content: '', styles: { fillColor: [226, 232, 240] } },
    { content: '', styles: { fillColor: [226, 232, 240] } },
    { content: `R$ ${fmt2(totalPecas2)}`, styles: { fontStyle: 'bold', fillColor: [226, 232, 240], halign: 'right' } },
    { content: `R$ ${fmt2(totalMao2)}`, styles: { fontStyle: 'bold', fillColor: [226, 232, 240], halign: 'right' } },
    { content: totalDesc2 > 0 ? `- R$ ${fmt2(totalDesc2)}` : '—', styles: { fillColor: [226, 232, 240], halign: 'right' } },
    { content: `R$ ${fmt2(totalGeral2)}`, styles: { fontStyle: 'bold', fillColor: [226, 232, 240], halign: 'right' } },
    { content: margem2, styles: { fontStyle: 'bold', fillColor: [226, 232, 240], halign: 'center' } },
  ]);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 6,
    head: [['OS', 'Cliente', 'Veículo', 'Peças', 'M. Obra', 'Desconto', 'Total', 'Margem']],
    body: linhasOS,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [30, 64, 175], fontStyle: 'bold', fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: 50 },
      2: { cellWidth: 72 },
      3: { cellWidth: 28, halign: 'right' },
      4: { cellWidth: 28, halign: 'right' },
      5: { cellWidth: 26, halign: 'right' },
      6: { cellWidth: 30, halign: 'right' },
      7: { cellWidth: 20, halign: 'center' },
    },
    margin: { left: 10, right: 10 },
  });

  return compartilharOuAbrirPDF(doc, `detalhamento-${label.toLowerCase().replace(/\s+/g, '-')}.pdf`);
}

export function gerarExcelDetalhamento(servicos, label, config) {
  // Custo total = custo de peças + custo de mão de obra — margem calculada sobre o
  // faturamento total da OS (peças + mão de obra), não só sobre peças.
  const calcCusto = s => (
    (s.pecas || []).reduce((a, p) => a + (p.custoUnitario || 0) * (p.quantidade || 1), 0) +
    (s.maosDeObra || []).reduce((a, m) => a + (m.custoUnitario || 0), 0)
  );

  const linhas = [...servicos]
    .sort((a, b) => b.totalGeral - a.totalGeral)
    .map(s => {
      const custo = calcCusto(s);
      const lucroOS = (s.totalGeral || 0) - custo;
      const margemOS = custo > 0 && s.totalGeral > 0 ? parseFloat((lucroOS / s.totalGeral * 100).toFixed(1)) : '';
      return {
        'OS': `OS #${s.numero}`,
        'Cliente': s.cliente?.nome || '',
        'Veículo': s.veiculo ? `${s.veiculo.placa} ${s.veiculo.marca} ${s.veiculo.modelo}` : '',
        'Peças (R$)': s.totalPecas || 0,
        'Mão de Obra (R$)': s.totalMaoDeObra || 0,
        'Desconto (R$)': s.desconto || 0,
        'Total (R$)': s.totalGeral || 0,
        'Custo Total (R$)': custo || '',
        'Lucro Total (R$)': custo > 0 ? lucroOS : '',
        'Margem (%)': margemOS,
      };
    });

  // Totais
  const totalGeral = servicos.reduce((a, s) => a + (s.totalGeral || 0), 0);
  const totalPecas = servicos.reduce((a, s) => a + (s.totalPecas || 0), 0);
  const totalMao   = servicos.reduce((a, s) => a + (s.totalMaoDeObra || 0), 0);
  const totalDesc  = servicos.reduce((a, s) => a + (s.desconto || 0), 0);
  const totalCusto = servicos.reduce((a, s) => a + calcCusto(s), 0);
  const lucroTotal = totalGeral - totalCusto;
  linhas.push({});
  linhas.push({
    'OS': 'TOTAL',
    'Peças (R$)': totalPecas,
    'Mão de Obra (R$)': totalMao,
    'Desconto (R$)': totalDesc,
    'Total (R$)': totalGeral,
    'Custo Total (R$)': totalCusto,
    'Lucro Total (R$)': lucroTotal,
    'Margem (%)': totalGeral > 0 && totalCusto > 0 ? parseFloat(((lucroTotal / totalGeral) * 100).toFixed(1)) : '',
  });

  const ws = XLSX.utils.json_to_sheet(linhas);
  ws['!cols'] = [{ wch: 10 }, { wch: 28 }, { wch: 32 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 12 }];

  // Aba de resumo
  const margem = totalGeral > 0 && totalCusto > 0 ? parseFloat(((lucroTotal / totalGeral) * 100).toFixed(1)) : '';
  const resumoRows = [
    { 'Indicador': 'Faturamento Total',   'Valor (R$)': totalGeral },
    { 'Indicador': 'Peças (venda)',        'Valor (R$)': totalPecas },
    { 'Indicador': 'Mão de Obra',          'Valor (R$)': totalMao },
    { 'Indicador': 'Descontos',            'Valor (R$)': totalDesc },
    { 'Indicador': 'Custo Total (Peças + M. Obra)', 'Valor (R$)': totalCusto },
    { 'Indicador': 'Lucro Total',          'Valor (R$)': lucroTotal },
    { 'Indicador': 'Margem Total (%)',     'Valor (R$)': margem },
    { 'Indicador': 'Qtd OS Concluídas',   'Valor (R$)': servicos.length },
    { 'Indicador': 'Ticket Médio',         'Valor (R$)': servicos.length > 0 ? parseFloat((totalGeral / servicos.length).toFixed(2)) : '' },
  ];
  const wsResumo = XLSX.utils.json_to_sheet(resumoRows);
  wsResumo['!cols'] = [{ wch: 26 }, { wch: 18 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, label.slice(0, 31));
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo');
  XLSX.writeFile(wb, `detalhamento-${label.toLowerCase().replace(/\s+/g, '-')}.xlsx`);
}
